import React, { memo, useEffect, useRef, useState } from 'react';
import { colorPalette } from '@sharefiledev/antd-config';
import {
	colourCount,
	Component,
	ComponentTypes,
	defaultComponentDimensions,
	defaultComponentHandles,
	defaultWidth,
	Direction,
	DirectionValueType,
	MERGE_FIELD_STATES,
	OFFSET_X,
	SIGNER_COLORS,
} from '../../../common/componentUtils';
import { StaticField } from '../../../components/OverlayerPage/StaticField';
import { DataPreFilledType } from '../../../constants';
import { StyledPageComponent, StyledResizable } from './Resizable.styled';

export interface Props {
	element: Component;
	handleComponentUpdate: (comp: Component[], val: string) => void;
	hideGhostComponent: () => void;
	setComponentsToEdit: (component: Component[]) => void;
	pageBoundingClientRect: { width: number; height: number; actualPageHeight: number };
	componentsToEdit: Component[];
	openSignatureModal: any;
	pageHeight: number;
	scale: number;
	areFieldsEditable: boolean;
	updateBoundingDimensions: (comp: any) => void;
}

export const Resizable = memo(
	({
		element: component,
		handleComponentUpdate,
		hideGhostComponent,
		setComponentsToEdit,
		pageBoundingClientRect,
		componentsToEdit,
		openSignatureModal,
		pageHeight,
		scale,
		areFieldsEditable,
		updateBoundingDimensions,
	}: Props) => {
		const signerSequence = component.assignedTo?.sequence ?? -1;

		const isMergeField =
			component.dataPreFilledType === DataPreFilledType.SourceMapped ||
			component.dataPreFilledType === DataPreFilledType.Manual;

		const componentColor = isMergeField
			? component.value
				? MERGE_FIELD_STATES.filled
				: MERGE_FIELD_STATES.unfilled
			: SIGNER_COLORS[signerSequence % (colourCount - 1)];

		const ref = useRef<HTMLDivElement>(null);

		// Calculate the top position and height based on page dimensions to handle mixed page sizes and orientations
		// component height is in ratio to the page height
		const componentHeight = component.height * pageBoundingClientRect.height;
		// Max top position to ensure the component does not overflow the page
		const maxTop = pageBoundingClientRect.actualPageHeight - componentHeight;
		// Calculate the top position based on the page height to handle mixed page sizes and orientations
		const componentTopPosition = Math.min(
			component.y * pageBoundingClientRect.height,
			maxTop
		);

		const [componentState, setComponentState] = useState({
			x: component.x * pageBoundingClientRect.width, // left position of element on x co-ordinate
			y: componentTopPosition, // top position of element on y co-ordinate
			width: component.width * pageBoundingClientRect.width,
			height: componentHeight,
			initialClientX: 0, // initial cursor position on x co-ordinate
			initialClientY: 0, // initial cursor position on y co-ordinate
			direction: '',
			isResizing: false,
			isDragging: false,
			minX: -1,
			minY: -1,
		});

		const onMouseOver = e => {
			e.stopPropagation();
			hideGhostComponent();
		};

		const onDoubleClick = () => {
			if (
				component.isAnnotation &&
				component.type === ComponentTypes.SignatureAnnotationComponent
			) {
				openSignatureModal();
			}
		};

		const onDragStart = event => {
			event.stopPropagation();
			setComponentsToEdit([component]);

			const { clientX, clientY } = event;

			hideGhostComponent();

			setComponentState(previousComponentState => ({
				...previousComponentState,
				initialClientX: clientX,
				initialClientY: clientY,
				direction: '',
				isDragging: true,
			}));
		};

		const onResizeStart = direction => (event: any) => {
			event.stopPropagation();

			const { clientX, clientY } = event;

			hideGhostComponent();
			const { minHeight, minWidth } = defaultComponentDimensions[component.type];
			const { right, bottom } = ref.current?.getBoundingClientRect()!;

			const minX = right - minWidth * scale;
			const minY = bottom - minHeight * scale;

			setComponentState(previousComponentState => ({
				...previousComponentState,
				initialClientX: clientX,
				initialClientY: clientY,
				direction,
				isResizing: true,
				isDragging: false,
				minX,
				minY,
			}));
			setComponentsToEdit([component]);
		};

		useEffect(() => {
			const node: HTMLDivElement = ref.current!;
			const parentElement = node.parentElement!;

			const {
				x: parentX,
				y: parentY,
				width: parentWidth,
			} = parentElement.getBoundingClientRect();

			const onResize = (clientX: number, clientY: number) => {
				const { aspectRatio, lockAspectRatio, minHeight, minWidth } =
					defaultComponentDimensions[component.type];
				const {
					x,
					y,
					width,
					height,
					direction,
					initialClientX,
					initialClientY,
					minX,
					minY,
				} = componentState;

				let newWidth = width;
				let newHeight = height;
				let newX = x;
				let newY = y;

				const diffY = clientY - initialClientY;
				const diffX = clientX - initialClientX;

				const resizeTop = () => {
					newHeight = height - diffY;
					newY = y + diffY;
					if (lockAspectRatio) {
						newWidth = newHeight / aspectRatio;
					}
				};
				const resizeRight = () => {
					newWidth = width + diffX;
					if (lockAspectRatio) {
						newWidth = newHeight * aspectRatio;
					}
				};
				const resizeBottom = () => {
					newHeight = height + diffY;

					if (lockAspectRatio) {
						newWidth = newHeight / aspectRatio;
					}
				};
				const resizeLeft = () => {
					newWidth = width - diffX;
					newX = x + diffX;
					if (lockAspectRatio) {
						if (direction === Direction.TopLeft) {
							newWidth = newHeight * aspectRatio;
							newX = x + width - newWidth;
						} else {
							newHeight = newWidth / aspectRatio;
						}
					}
				};

				switch (direction) {
					case Direction.Top:
						resizeTop();
						break;
					case Direction.Bottom:
						resizeBottom();
						break;
					case Direction.Right:
						resizeRight();
						break;
					case Direction.Left:
						resizeLeft();
						break;
					case Direction.TopRight:
						resizeTop();
						resizeRight();
						break;
					case Direction.TopLeft:
						resizeTop();
						resizeLeft();
						break;
					case Direction.BottomRight:
						resizeBottom();
						resizeRight();
						break;
					case Direction.BottomLeft:
						resizeBottom();
						resizeLeft();
						break;
				}

				const offset = 0;
				const borderWidth = 1;
				const maxWidth = defaultWidth;
				const scaledMinWidth = minWidth * scale;
				const scaledMinHeight = minHeight * scale;
				const maxX = Math.max(
					parentWidth -
						Math.min(Math.max(newWidth, scaledMinWidth), maxWidth) +
						borderWidth -
						offset -
						OFFSET_X,
					0
				);
				const maxY =
					pageHeight - Math.max(newHeight, scaledMinHeight) + borderWidth - offset;

				newX = Math.max(Math.min(newX, maxX), offset);
				newY = Math.max(Math.min(newY, maxY), offset);

				if (newX + parentX >= minX && minX > -1) {
					newX = minX - parentX;
				}
				if (newY + parentY >= minY && minY > -1) {
					newY = minY - parentY;
				}

				const finalHeight = Math.max(newHeight, scaledMinHeight);
				const finalWidth = Math.max(newWidth, scaledMinWidth);

				if (
					x === newX &&
					finalWidth === scaledMinWidth &&
					(direction === Direction.Left || direction === Direction.Right)
				) {
					return;
				}

				if (
					y === newY &&
					finalHeight === scaledMinHeight &&
					(direction === Direction.Top || direction === Direction.Bottom)
				) {
					return;
				}

				if (lockAspectRatio) {
					if (
						newX === maxX &&
						diffX > 0 &&
						(direction === Direction.Right ||
							direction === Direction.BottomRight ||
							direction === Direction.TopRight)
					) {
						return;
					}

					if (newX === 0 && diffX < 0 && direction === Direction.TopLeft) {
						return;
					}
				}

				setComponentState(previousComponentState => ({
					...previousComponentState,
					height: finalHeight,
					width: finalWidth,
					x: newX,
					y: newY,
					initialClientX: clientX,
					initialClientY: clientY,
				}));
				if (component.type === ComponentTypes.CheckGroupComponent) {
					updateBoundingDimensions({
						id: component.id,
						x: newX / pageBoundingClientRect.width,
						y: newY / pageBoundingClientRect.height,
						height: finalHeight / pageBoundingClientRect.height,
						width: finalWidth / pageBoundingClientRect.width,
					});
				}
			};

			const hasComponentChanged = elementCopy => {
				return (
					elementCopy.x !== componentState.x ||
					elementCopy.y !== componentState.y ||
					elementCopy.width !== componentState.width ||
					elementCopy.height !== componentState.height
				);
			};

			const updateComponent = () => {
				const elementCopy = { ...component };
				if (hasComponentChanged(elementCopy)) {
					elementCopy.x = componentState.x / pageBoundingClientRect.width;
					elementCopy.y = componentState.y / pageBoundingClientRect.height;
					elementCopy.width = componentState.width / pageBoundingClientRect.width;
					elementCopy.height = componentState.height / pageBoundingClientRect.height;
					handleComponentUpdate([elementCopy], 'update');
				}
			};

			const onDragOrResizeFinish = event => {
				if (componentState.isResizing) {
					event.stopPropagation();
					setComponentState(previousComponentState => ({
						...previousComponentState,
						direction: '',
						isResizing: false,
						minX: -1,
						minY: -1,
					}));
					updateComponent();
				}

				if (componentState.isDragging) {
					event.stopPropagation();
					setComponentState(previousComponentState => ({
						...previousComponentState,
						isDragging: false,
					}));
					updateComponent();
				}
			};

			const onDrag = (event: MouseEvent) => {
				event.stopPropagation();
				const { minHeight, minWidth } = defaultComponentDimensions[component.type];
				const scaledMinWidth = minWidth * scale;
				const scaledMinHeight = minHeight * scale;
				const { x, y, initialClientX, initialClientY, width, height } = componentState;
				const offset = 0;
				const borderWidth = 1;
				const maxX =
					parentWidth - Math.max(width, scaledMinWidth) + borderWidth - offset - OFFSET_X;
				const maxY =
					pageHeight - Math.max(height, scaledMinHeight) + borderWidth - offset;

				let newX = x + event.clientX - initialClientX;
				let newY = y + event.clientY - initialClientY;

				newX = Math.max(Math.min(newX, maxX), offset);
				newY = Math.max(Math.min(newY, maxY), offset);

				setComponentState(previousComponentState => ({
					...previousComponentState,
					x: newX,
					y: newY,
					initialClientX: event.clientX,
					initialClientY: event.clientY,
				}));
				if (component.type === ComponentTypes.CheckGroupComponent) {
					updateBoundingDimensions({
						id: component.id,
						x: newX / pageBoundingClientRect.width,
						y: newY / pageBoundingClientRect.height,
						height: componentState.height / pageBoundingClientRect.height,
						width: componentState.width / pageBoundingClientRect.width,
					});
				}
			};

			const handleMouseMove = (e: MouseEvent) => {
				if (componentState.isResizing) {
					e.stopPropagation();
					onResize(e.clientX, e.clientY);
				}

				if (componentState.isDragging) {
					e.stopPropagation();
					onDrag(e);
				}
			};

			if (parentElement && areFieldsEditable) {
				parentElement.addEventListener('mousemove', handleMouseMove);
				parentElement.addEventListener('mouseup', onDragOrResizeFinish);
				parentElement.addEventListener('mouseleave', onDragOrResizeFinish);
			}

			return () => {
				if (parentElement && areFieldsEditable) {
					parentElement.removeEventListener('mousemove', handleMouseMove);
					parentElement.removeEventListener('mouseup', onDragOrResizeFinish);
					parentElement.removeEventListener('mouseleave', onDragOrResizeFinish);
				}
			};
		}, [
			componentState,
			hideGhostComponent,
			handleComponentUpdate,
			component,
			pageBoundingClientRect,
			componentsToEdit,
			scale,
			pageHeight,
			updateBoundingDimensions,
			areFieldsEditable,
		]);

		useEffect(() => {
			setComponentState(oldState => {
				return {
					...oldState,
					x: component.x * pageBoundingClientRect.width, // left position of element on x co-ordinate
					y: component.y * pageBoundingClientRect.height, // top position of element on y co-ordinate
					width: component.width * pageBoundingClientRect.width,
					height: component.height * pageBoundingClientRect.height,
				};
			});
		}, [pageBoundingClientRect, component]);

		const style = {
			position: 'absolute',
			left: componentState.x + 'px',
			top: componentState.y + 'px',
			width: componentState.width + 'px',
			height: componentState.height + 'px',
			minHeight: defaultComponentDimensions[component.type].minHeight * scale + 'px',
			minWidth: defaultComponentDimensions[component.type].minWidth * scale + 'px',
			cursor: 'move',
			userSelect: 'none',
			pointerEvents: areFieldsEditable ? 'all' : 'none',
		} as React.CSSProperties;

		const handleEnablers = defaultComponentHandles[component.type];

		const [textAnnotationZIndex, setTextAnnotationZIndex] = React.useState(0);
		function handleResizableDoubleClick() {
			setTextAnnotationZIndex(1);
		}

		const eventHandlersForEditableFields = areFieldsEditable
			? {
					onMouseOver,
					onMouseDown: event => {
						onDragStart(event);
					},
					onDoubleClick,
			  }
			: {};

		return (
			<div
				style={style}
				id={`component${component.id}`}
				{...eventHandlersForEditableFields}
				ref={ref}
				role="none"
			>
				<StyledPageComponent
					isAnnotation={component.isAnnotation}
					isTextAnnotation={
						component.isAnnotation &&
						component.type === ComponentTypes.TextAnnotationComponent
					}
					componentColor={componentColor?.name}
					isError={component.showErrorOnResizable}
					hideBorder={isMergeField && !component.value && !areFieldsEditable}
				>
					<StaticField
						component={component}
						width={componentState.width}
						height={componentState.height}
						handleComponentUpdate={handleComponentUpdate}
						setComponentsToEdit={setComponentsToEdit}
						componentsToEdit={componentsToEdit}
						textAnnotationZIndex={textAnnotationZIndex}
						updateTextAnnotationZIndex={(zIndex: number) =>
							setTextAnnotationZIndex(zIndex)
						}
						scale={scale}
					/>
					{areFieldsEditable && (
						<StyledResizable
							isTextAnnotation={
								component.isAnnotation &&
								component.type === ComponentTypes.TextAnnotationComponent
							}
							isAnnotation={component.isAnnotation}
							isSameComponent={componentsToEdit?.[0]?.id === component.id}
							componentColor={componentColor?.color}
							showErrorOnResizable={component.showErrorOnResizable}
							onDoubleClick={handleResizableDoubleClick}
						>
							{handleEnablers.map((direction, index) => (
								<Resizer
									showHandles={componentsToEdit?.[0]?.id === component.id}
									key={index}
									onResizeStart={onResizeStart(direction)}
									direction={direction}
									onMouseOver={onMouseOver}
									isAnnotation={component.isAnnotation}
									componentColor={componentColor}
									componentHasError={!!component.showErrorOnResizable}
								/>
							))}
						</StyledResizable>
					)}
				</StyledPageComponent>
			</div>
		);
	}
);

interface ResizerProps {
	direction: DirectionValueType;
	onResizeStart: (event: any) => void;
	onMouseOver: (event: any) => void;
	isAnnotation: boolean;
	showHandles: boolean;
	componentColor?: any;
	componentHasError: boolean;
}

const Resizer = ({
	showHandles,
	direction,
	onResizeStart,
	onMouseOver,
	isAnnotation,
	componentColor,
	componentHasError,
}: ResizerProps) => {
	return (
		<div
			aria-label={`resizable-handle-${direction}`}
			className={`resizable-handle resizable-handle-${direction} ${
				isAnnotation ? 'resizable-handle-annotation' : ''
			}`}
			onMouseOver={onMouseOver}
			onMouseDown={onResizeStart}
			style={{
				display: showHandles ? 'block' : 'none',
				border: `2px solid ${
					componentHasError
						? colorPalette.red6
						: isAnnotation
						? colorPalette.neutral13
						: componentColor?.color
				}`,
			}}
			role="button"
		></div>
	);
};
